/**
 * Payout hold — data-layer client for Feature #138.
 *
 * Thin typed wrapper over `apiClient` for the payout-hold API surfaces:
 *   - GET  /api/v1/payout-hold/:affiliate_id       (current hold config)
 *   - PUT  /api/v1/payout-hold/:affiliate_id       (upsert hold days + reason)
 *   - GET  /api/v1/payout-hold/release-queue       (conversions past hold)
 *
 * Keep this file dependency-free beyond `apiClient` so the pure helpers
 * (formatting, validation, queryKeys) stay unit-testable in isolation.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirror the backend response shape declared in
// apps/api/src/routes/payout-hold.ts.
// ---------------------------------------------------------------------------

export const MIN_HOLD_DAYS = 0;
export const MAX_HOLD_DAYS = 120;
export const DEFAULT_HOLD_DAYS = 30;

export type HoldStatus = "active" | "paused";

export const HOLD_STATUSES: readonly HoldStatus[] = ["active", "paused"] as const;

export function isHoldStatus(v: unknown): v is HoldStatus {
	return typeof v === "string" && (HOLD_STATUSES as readonly string[]).includes(v);
}

export interface PayoutHold {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	hold_days: number;
	reason: string | null;
	effective_from: number;
	status: HoldStatus;
	created_at: number;
	updated_at: number;
	is_default: boolean;
}

export interface HoldResponse {
	hold: PayoutHold;
}

export interface ReleaseQueueItem {
	conversion_id: string;
	affiliate_id: string;
	conversion_ts: number;
	hold_days: number;
	earliest_payable_ts: number;
	payout_cents: number;
	currency: string | null;
}

export interface ReleaseQueueResponse {
	count: number;
	now: number;
	items: ReleaseQueueItem[];
}

// ---------------------------------------------------------------------------
// Validators (pure — unit-testable)
// ---------------------------------------------------------------------------

const AFFILIATE_ID_RE = /^[a-z0-9_-]{1,64}$/i;

export function isValidAffiliateId(v: unknown): v is string {
	return typeof v === "string" && AFFILIATE_ID_RE.test(v);
}

export function clampHoldDays(n: number): number {
	if (!Number.isFinite(n)) return DEFAULT_HOLD_DAYS;
	const i = Math.floor(n);
	if (i < MIN_HOLD_DAYS) return MIN_HOLD_DAYS;
	if (i > MAX_HOLD_DAYS) return MAX_HOLD_DAYS;
	return i;
}

export function isValidHoldDays(n: unknown): n is number {
	return (
		typeof n === "number" &&
		Number.isFinite(n) &&
		Number.isInteger(n) &&
		n >= MIN_HOLD_DAYS &&
		n <= MAX_HOLD_DAYS
	);
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatDate(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function formatMoneyCents(
	cents: number,
	currency: string | null,
): string {
	if (!Number.isFinite(cents)) return "—";
	const dollars = cents / 100;
	const code = currency ?? "USD";
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: code,
			minimumFractionDigits: 2,
		}).format(dollars);
	} catch {
		return `${dollars.toFixed(2)} ${code}`;
	}
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/payout-hold";

/** GET /api/v1/payout-hold/:affiliate_id */
export async function fetchPayoutHold(
	affiliateId: string,
): Promise<PayoutHold> {
	const res = await apiClient.get<HoldResponse>(
		`${BASE}/${encodeURIComponent(affiliateId)}`,
	);
	return res.hold;
}

export interface SetHoldArgs {
	affiliateId: string;
	holdDays: number;
	reason?: string | null;
	status?: HoldStatus;
}

/** PUT /api/v1/payout-hold/:affiliate_id */
export async function setPayoutHold(args: SetHoldArgs): Promise<PayoutHold> {
	const body: Record<string, unknown> = { hold_days: args.holdDays };
	if (args.reason !== undefined) body.reason = args.reason;
	if (args.status !== undefined) body.status = args.status;
	const res = await apiClient.put<HoldResponse>(
		`${BASE}/${encodeURIComponent(args.affiliateId)}`,
		body,
	);
	return res.hold;
}

export interface FetchReleaseQueueArgs {
	limit?: number;
	affiliateId?: string;
}

/** GET /api/v1/payout-hold/release-queue */
export async function fetchReleaseQueue(
	args: FetchReleaseQueueArgs = {},
): Promise<ReleaseQueueResponse> {
	const query: Record<string, string | number> = {};
	if (args.limit !== undefined) query.limit = args.limit;
	if (args.affiliateId !== undefined) query.affiliate_id = args.affiliateId;
	return apiClient.get<ReleaseQueueResponse>(`${BASE}/release-queue`, {
		query,
	});
}

// ---------------------------------------------------------------------------
// Query keys — used by TanStack Query for invalidation after mutations.
// ---------------------------------------------------------------------------

export const payoutHoldQueryKeys = {
	all: ["payout-hold"] as const,
	byAffiliate: (id: string) =>
		[...payoutHoldQueryKeys.all, "by-affiliate", id] as const,
	releaseQueue: (filters: { affiliateId?: string } = {}) =>
		[...payoutHoldQueryKeys.all, "release-queue", filters.affiliateId ?? "all"] as const,
};
